import { motion, AnimatePresence } from "framer-motion";
import { X, WarningCircle, Info, Warning } from "@phosphor-icons/react";
import { useContextStore, type Suggestion, type SuggestionSeverity } from "../../stores/context";
import { cn } from "../../lib/cn";

interface ContextSuggestionsProps {
  className?: string;
}

const SEVERITY_CONFIG: Record<SuggestionSeverity, {
  icon: typeof WarningCircle;
  border: string;
  bg: string;
  iconColor: string;
}> = {
  critical: {
    icon: WarningCircle,
    border: "border-[var(--context-critical-border)]",
    bg: "bg-[var(--context-critical-bg)]",
    iconColor: "text-[var(--color-soft-red)]",
  },
  warning: {
    icon: Warning,
    border: "border-[var(--context-suggestion-border)]",
    bg: "bg-[var(--context-suggestion-bg)]",
    iconColor: "text-[var(--color-ember-orange)]",
  },
  info: {
    icon: Info,
    border: "border-[var(--border-hairline)]",
    bg: "bg-[var(--bg-surface)]",
    iconColor: "text-[var(--color-cool-azure)]",
  },
};

const spring = { type: "spring" as const, stiffness: 300, damping: 25 };

function SuggestionCard({ suggestion }: { suggestion: Suggestion }) {
  const dismissSuggestion = useContextStore((s) => s.dismissSuggestion);
  const config = SEVERITY_CONFIG[suggestion.severity] ?? SEVERITY_CONFIG.info;
  const Icon = config.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 8, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 8, scale: 0.95 }}
      transition={spring}
      className={cn(
        "flex items-start gap-2 rounded-[var(--radius-md)] border px-2.5 py-2",
        config.border,
        config.bg,
      )}
    >
      <Icon size={13} weight="bold" className={cn("mt-0.5 shrink-0", config.iconColor)} />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-[var(--text-primary)] leading-relaxed">
          {suggestion.message}
        </p>
        {suggestion.section && (
          <p className="text-[9px] text-[var(--text-muted)] mt-0.5 uppercase tracking-wider">
            {suggestion.section}
          </p>
        )}
      </div>
      <button
        onClick={() => dismissSuggestion(suggestion.id)}
        className="shrink-0 mt-0.5 p-0.5 rounded-sm text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] transition-colors"
      >
        <X size={10} weight="bold" />
      </button>
    </motion.div>
  );
}

function ContextSuggestions({ className }: ContextSuggestionsProps) {
  const suggestions = useContextStore((s) => s.suggestions);
  const dismissedIds = useContextStore((s) => s.dismissedIds);
  const isLoading = useContextStore((s) => s.isLoadingSuggestions);
  const visible = suggestions.filter((s) => !dismissedIds.has(s.id));

  if (visible.length === 0 && !isLoading) return null;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {isLoading && visible.length === 0 && (
        <div className="flex items-center gap-2 px-2 py-2">
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-[var(--color-lever-peach)] border-t-transparent" />
          <span className="text-[10px] text-[var(--text-muted)]">Analyzing...</span>
        </div>
      )}
      <AnimatePresence mode="popLayout">
        {visible.map((s) => (
          <SuggestionCard key={s.id} suggestion={s} />
        ))}
      </AnimatePresence>
    </div>
  );
}

ContextSuggestions.displayName = "ContextSuggestions";

export { ContextSuggestions };
export type { ContextSuggestionsProps };
